import { Component, OnInit, Inject, OnDestroy, ChangeDetectorRef, TemplateRef, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PickingsService } from '@app/services/pickings/picking.service';
import { UsersService } from '@app/services/users/users.service';
import { TranslateService } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { selectSettingsState } from '@app/modules/settings/store/selectors/settings.selectors';
import { ImageUploadService } from '@app/services/image-upload/image-upload.service';
import { first } from 'rxjs/operators';
import { LabelPrintMenuService, PrintMenuOption } from '@app/services/labels/label-print-menu.service';

export interface AssetBorrowDialogData {
  prdID: number;
  prdNameEn: string;
  prdCode: string;
  prdAssetsNumber?: string;
  prdBarcode?: string;
  prdRetail?: string;
  storageInfo?: {
    storageStructureID: number;
    storagePath: string;
  };
}

export interface ActiveBorrow {
  productMovementHistoryID: number;
  borrowedByName: string;
  borrowedByUsrID: number;
  destinationNote: string;
  expectedReturnDate: string;
  quantity: number;
  remainingQuantity: number;
  created_at: string;
  storageStructureID: number;
  storagePath: string;
}

export interface StorageOption {
  storageStructureID: number;
  storagePath: string;
  net_quantity: number;
  onHand?: number;
}

export interface BorrowHistoryItem {
  productMovementHistoryID: number;
  type: 'borrow' | 'return';
  userName: string;
  destinationNote: string;
  created_at: string;
  quantity?: number;
  damagePhotoCount?: number;
}

@Component({
  selector: 'app-asset-borrow-dialog',
  templateUrl: './asset-borrow-dialog.component.html',
  styleUrls: ['./asset-borrow-dialog.component.scss']
})
export class AssetBorrowDialogComponent implements OnInit, OnDestroy {
  public borrowForm: FormGroup;
  public adjustmentForm: FormGroup;
  public activeBorrows: ActiveBorrow[] = [];
  public borrowHistory: BorrowHistoryItem[] = [];
  public storages: StorageOption[] = [];
  public allStorages: StorageOption[] = [];
  public users: any[] = [];
  public isLoading: boolean = true;
  public isSaving: boolean = false;
  public selectedTabIndex: number = 0;
  public hasChanges: boolean = false;
  public minDate: Date = new Date(); // Today - prevent past dates
  // Per-borrow return quantity for partial returns, keyed by movement ID
  public returnQty: { [productMovementHistoryID: number]: number } = {};
  // Damage photos selected per borrow; uploaded after the return succeeds
  public damageFiles: { [productMovementHistoryID: number]: File[] } = {};
  // Loaded damage photo object URLs per return history item (toggled on click)
  public historyPhotos: { [productMovementHistoryID: number]: SafeUrl[] } = {};
  // Photo currently shown enlarged in the viewer modal
  public selectedPhoto: SafeUrl | null = null;

  @ViewChild('photoViewer') photoViewerTpl: TemplateRef<any>;
  public locEnablePrinting: boolean = false;
  public locEnableBrotherPrinting: boolean = false;

  private subs: SubSink = new SubSink();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AssetBorrowDialogData,
    private dialogRef: MatDialogRef<AssetBorrowDialogComponent>,
    private fb: FormBuilder,
    private _picking: PickingsService,
    private _users: UsersService,
    private _translate: TranslateService,
    private _toastr: ToastrService,
    private _cdr: ChangeDetectorRef,
    private _sanitizer: DomSanitizer,
    private _dialog: MatDialog,
    private store$: Store,
    private _imageUpload: ImageUploadService,
    private _labelMenu: LabelPrintMenuService
  ) {
    this.borrowForm = this.fb.group({
      storageStructureID: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      borrowedByUsrID: ['', Validators.required],
      destinationNote: [''],
      expectedReturnDate: ['']
    });

    this.adjustmentForm = this.fb.group({
      storageStructureID: ['', Validators.required],
      newQuantity: [0, [Validators.required, Validators.min(0)]],
      reason: ['']
    });
  }

  ngOnInit(): void {
    console.log('AssetBorrowDialog data:', this.data);
    this.loadData();
    this.loadPrinterSettings();
    this.loadDymoScript();
    this.loadBrotherScript();
    this._imageUpload.cacheLogoForLabelPrinting();

    /* The labels the customer designed themselves. This menu is hand-written
       rather than driven by a printLabelList, so they are loaded straight into
       an array the template renders after the built-in entries. */
    this._labelMenu.options().pipe(first()).subscribe((options) => {
      this.customLabels = options;
      this._cdr.markForCheck();
    });

    // Closing via backdrop/ESC must still report whether data changed,
    // so the product list refreshes after partial returns too.
    this.dialogRef.disableClose = true;
    this.subs.add(this.dialogRef.backdropClick().subscribe(() => this.onClose()));
    this.subs.add(this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.onClose();
      }
    }));
  }

  private loadDymoScript(): void {
    // Check if script already loaded
    if (document.querySelector('script[src*="printerCode"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'assets/scripts/printerCode.js?v9';
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);
  }

  private loadBrotherScript(): void {
    // Check if script already loaded
    if (document.querySelector('script[src*="brotherPrinterCode"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'assets/brother/brotherPrinterCode.js';
    script.type = 'module';
    script.async = true;
    document.head.appendChild(script);
  }

  private loadPrinterSettings(): void {
    this.subs.add(
      this.store$.select(selectSettingsState).subscribe(settings => {
        this.locEnablePrinting = settings?.locEnablePrinting === 1;
        this.locEnableBrotherPrinting = settings?.locEnableBrotherPrinting === 1;
      })
    );
  }

  printDymo(): void {
    console.log('Print DYMO - Asset Number:', this.data.prdAssetsNumber);

    if (!this.data.prdAssetsNumber || this.data.prdAssetsNumber.trim() === '') {
      this._toastr.warning(this._translate.instant('asset.assetNumberRequired'));
      return;
    }

    // Get storage info for QR code
    const storage = this.getSelectedStorage();
    const storageStructureID = storage?.storageStructureID || 0;

    // Set data in localStorage for DYMO print function
    localStorage.setItem('assetNumber', this.data.prdAssetsNumber);
    localStorage.setItem('assetProductName', this.data.prdNameEn);
    localStorage.setItem('assetPrdID', String(this.data.prdID));
    localStorage.setItem('assetStorageStructureID', String(storageStructureID));

    // Call DYMO print function
    if (typeof (window as any).printAssetLabel === 'function') {
      (window as any).printAssetLabel();
    } else {
      this._toastr.error('DYMO printer not available');
    }
  }

  printShippingLabelQrOnly(): void {
    this.printShippingLabel('printShippingLabelQrOnly');
  }

  printShippingLabelBarcodeOnly(): void {
    this.printShippingLabel('printShippingLabelBarcodeOnly');
  }

  private printShippingLabel(printFunctionName: string): void {
    // Get storage info for QR code
    const storage = this.getSelectedStorage();
    const storageStructureID = storage?.storageStructureID || 0;

    // Set data in localStorage for DYMO print function
    localStorage.setItem('prdNameEn', this.data.prdNameEn || '');
    localStorage.setItem('prdCode', this.data.prdCode || '');
    localStorage.setItem('prdBarcode', this.data.prdBarcode || '');
    localStorage.setItem('prdAssetsNumber', this.data.prdAssetsNumber || '');
    localStorage.setItem('prdRetail', this.data.prdRetail || '');
    localStorage.setItem('prdID', String(this.data.prdID));
    localStorage.setItem('storageStructureID', String(storageStructureID));
    // This dialog only ever opens for asset products
    localStorage.setItem('prdIsAsset', '1');

    // Call DYMO print function
    if (typeof (window as any)[printFunctionName] === 'function') {
      (window as any)[printFunctionName]();
    } else {
      this._toastr.error('DYMO printer not available');
    }
  }

  /** Labels this customer designed, offered alongside the built-in ones. */
  public customLabels: PrintMenuOption[] = [];

  /**
   * Print one of the customer's own labels for this asset.
   *
   * Fills the same localStorage keys the built-in asset labels use, so a
   * designed label sees exactly the data the fixed ones do, then hands off to
   * the print bridge by the template id.
   */
  printDesigned(option: PrintMenuOption): void {
    const storage = this.getSelectedStorage();
    const storageStructureID = storage?.storageStructureID || 0;

    localStorage.setItem('prdNameEn', this.data.prdNameEn || '');
    localStorage.setItem('prdCode', this.data.prdCode || '');
    localStorage.setItem('prdBarcode', this.data.prdBarcode || '');
    localStorage.setItem('prdAssetsNumber', this.data.prdAssetsNumber || '');
    localStorage.setItem('prdRetail', this.data.prdRetail || '');
    localStorage.setItem('prdID', String(this.data.prdID));
    localStorage.setItem('storageStructureID', String(storageStructureID));
    localStorage.setItem('storagePrint', 'false');
    // This dialog only ever opens for asset products
    localStorage.setItem('prdIsAsset', '1');
    localStorage.setItem('labelType', String(option.value));

    const print = (window as any).printSelected;
    if (typeof print === 'function') {
      print();
    } else {
      this._toastr.error(this._translate.instant('labelSheet.failed'));
    }
  }

  printBrother(): void {
    if (!this.data.prdAssetsNumber) {
      this._toastr.warning(this._translate.instant('asset.assetNumberRequired'));
      return;
    }

    // Get storage info for QR code
    const storage = this.getSelectedStorage();
    const storageStructureID = storage?.storageStructureID || 0;

    // Call Brother print function
    if (typeof (window as any).printAssetLabelBrother === 'function') {
      (window as any).printAssetLabelBrother(
        this.data.prdAssetsNumber,
        this.data.prdNameEn,
        this.data.prdID,
        storageStructureID
      );
    } else {
      this._toastr.error('Brother printer not available');
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadData(): void {
    this.isLoading = true;

    // Load all data in parallel
    Promise.all([
      this.loadActiveBorrows(),
      this.loadStorages(),
      this.loadUsers(),
      this.loadAllStorages(),
      this.loadBorrowHistory()
    ]).finally(() => {
      this.isLoading = false;
      this._cdr.detectChanges();
    });
  }

  private loadActiveBorrows(): Promise<void> {
    return new Promise((resolve) => {
      this.subs.add(
        this._picking.getAssetBorrows(this.data.prdID).subscribe({
          next: (res) => {
            this.activeBorrows = res.borrows || [];
            this.initReturnQuantities();
            resolve();
          },
          error: (err) => {
            console.error('Error loading active borrows:', err);
            this.activeBorrows = [];
            resolve();
          }
        })
      );
    });
  }

  // Default each borrow's return quantity to its full remaining amount
  private initReturnQuantities(): void {
    this.returnQty = {};
    this.activeBorrows.forEach(b => {
      this.returnQty[b.productMovementHistoryID] = b.remainingQuantity;
    });
  }

  private loadStorages(): Promise<void> {
    return new Promise((resolve) => {
      this.subs.add(
        this._picking.getPickingProductOnHand(this.data.prdID).subscribe({
          next: (res) => {
            // Filter storages with positive quantity
            this.storages = (res.productOnHand || []).filter((s: StorageOption) => s.net_quantity > 0);

            // Pre-select the first storage; quantity defaults to 1 and is
            // capped to the selected storage's available amount.
            if (this.storages.length > 0) {
              this.borrowForm.patchValue({
                storageStructureID: this.storages[0].storageStructureID,
                quantity: 1
              });
              this.onStorageChange();
            } else if (this.data.storageInfo) {
              // Fall back to the storage info passed into the dialog
              this.storages = [{
                storageStructureID: this.data.storageInfo.storageStructureID,
                storagePath: this.data.storageInfo.storagePath,
                net_quantity: 1
              }];
              this.borrowForm.patchValue({
                storageStructureID: this.data.storageInfo.storageStructureID,
                quantity: 1
              });
              this.onStorageChange();
            }
            resolve();
          },
          error: (err) => {
            console.error('Error loading storages:', err);
            // On error, fall back to the storage info passed into the dialog
            if (this.data.storageInfo) {
              this.storages = [{
                storageStructureID: this.data.storageInfo.storageStructureID,
                storagePath: this.data.storageInfo.storagePath,
                net_quantity: 1
              }];
              this.borrowForm.patchValue({
                storageStructureID: this.data.storageInfo.storageStructureID,
                quantity: 1
              });
              this.onStorageChange();
            } else {
              this.storages = [];
            }
            resolve();
          }
        })
      );
    });
  }

  private loadAllStorages(): Promise<void> {
    return new Promise((resolve) => {
      this.subs.add(
        this._picking.getPickingProductOnHand(this.data.prdID).subscribe({
          next: (res) => {
            // Include all storages (even with 0 quantity) for stock adjustment
            this.allStorages = (res.productOnHand || []).map((s: StorageOption) => ({
              ...s,
              onHand: s.net_quantity || 0
            }));
            resolve();
          },
          error: (err) => {
            console.error('Error loading all storages:', err);
            this.allStorages = [];
            resolve();
          }
        })
      );
    });
  }

  private loadUsers(): Promise<void> {
    return new Promise((resolve) => {
      this.subs.add(
        this._users.onGetUsers('usrFirstName', 'asc').subscribe({
          next: (res) => {
            // API returns { result, data: { users, total } }
            this.users = res.data?.users || res.users || [];
            resolve();
          },
          error: (err) => {
            console.error('Error loading users:', err);
            this.users = [];
            resolve();
          }
        })
      );
    });
  }

  private loadBorrowHistory(): Promise<void> {
    return new Promise((resolve) => {
      this.subs.add(
        this._picking.getAssetBorrowHistory(this.data.prdID).subscribe({
          next: (res) => {
            this.borrowHistory = res.history || [];
            resolve();
          },
          error: (err) => {
            console.error('Error loading borrow history:', err);
            this.borrowHistory = [];
            resolve();
          }
        })
      );
    });
  }

  onBorrow(): void {
    if (this.borrowForm.valid && !this.isSaving) {
      this.isSaving = true;

      const formData = {
        ...this.borrowForm.value,
        prdID: this.data.prdID
      };

      this.subs.add(
        this._picking.createAssetBorrow(formData).subscribe({
          next: (res) => {
            if (!res.error) {
              // Toastr is handled by HTTP interceptor via res.data.toastr
              this.hasChanges = true;

              // Reset form
              this.borrowForm.reset({ quantity: 1 });

              // Reload data
              this.refreshData();

              // Switch to active borrows tab
              this.selectedTabIndex = 0;
            } else {
              this._toastr.error(res.message || 'Error checking out asset');
            }
            this.isSaving = false;
          },
          error: (err) => {
            console.error('Error creating borrow:', err);
            this._toastr.error('Error checking out asset');
            this.isSaving = false;
          }
        })
      );
    }
  }

  onReturn(borrow: ActiveBorrow): void {
    if (this.isSaving) return;

    const qty = Math.floor(Number(this.returnQty[borrow.productMovementHistoryID] ?? borrow.remainingQuantity));
    if (!qty || qty < 1 || qty > borrow.remainingQuantity) {
      this._toastr.warning(this._translate.instant('asset.invalidReturnQuantity'));
      return;
    }

    this.isSaving = true;

    this.subs.add(
      this._picking.returnAsset(borrow.productMovementHistoryID, qty).subscribe({
        next: (res) => {
          if (!res.error) {
            const files = this.damageFiles[borrow.productMovementHistoryID] || [];
            const returnID = res.return?.productMovementHistoryID;

            const finish = () => {
              delete this.damageFiles[borrow.productMovementHistoryID];
              if (qty >= borrow.remainingQuantity) {
                // Fully returned - close dialog and force refresh
                this.dialogRef.close({ refresh: true });
              } else {
                // Partial return - stay open so the remaining amount is visible
                this.hasChanges = true;
                this.refreshData();
                this.isSaving = false;
              }
            };

            if (files.length && returnID) {
              // Attach damage photos to the freshly created return record
              forkJoin(files.map(f => this._picking.uploadAssetReturnPhoto(returnID, f))).subscribe({
                next: () => finish(),
                error: (err) => {
                  console.error('Error uploading damage photos:', err);
                  this._toastr.warning(this._translate.instant('asset.damagePhotoUploadFailed'));
                  finish();
                }
              });
            } else {
              finish();
            }
          } else {
            this._toastr.error(res.message || 'Error returning asset');
            this.isSaving = false;
          }
        },
        error: (err) => {
          console.error('Error returning asset:', err);
          this._toastr.error('Error returning asset');
          this.isSaving = false;
        }
      })
    );
  }

  onDamagePhotoSelected(borrow: ActiveBorrow, event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length) {
      const key = borrow.productMovementHistoryID;
      this.damageFiles[key] = (this.damageFiles[key] || []).concat(files);
    }
    input.value = '';
  }

  clearDamagePhotos(borrow: ActiveBorrow): void {
    delete this.damageFiles[borrow.productMovementHistoryID];
  }

  // Show a damage photo enlarged in a modal
  openPhotoViewer(photo: SafeUrl): void {
    this.selectedPhoto = photo;
    this._dialog.open(this.photoViewerTpl, {
      maxWidth: '95vw',
      autoFocus: false
    });
  }

  // Toggle the damage photo thumbnails of a return history item
  toggleDamagePhotos(item: BorrowHistoryItem): void {
    const key = item.productMovementHistoryID;
    if (this.historyPhotos[key]) {
      delete this.historyPhotos[key];
      return;
    }

    const count = item.damagePhotoCount || 0;
    if (count === 0) return;

    const requests = Array.from({ length: count }, (_, i) => this._picking.getAssetReturnPhoto(key, i));
    this.subs.add(
      forkJoin(requests).subscribe({
        next: (blobs) => {
          this.historyPhotos[key] = blobs.map(b => this._sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(b)));
          this._cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading damage photos:', err);
        }
      })
    );
  }

  private refreshData(): void {
    // Reload active borrows
    this._picking.getAssetBorrows(this.data.prdID).subscribe({
      next: (res) => {
        this.activeBorrows = res.borrows || [];
        this.initReturnQuantities();
      },
      error: (err) => {
        console.error('Error reloading borrows:', err);
      }
    });

    // Reload storages
    this._picking.getPickingProductOnHand(this.data.prdID).subscribe({
      next: (res) => {
        this.storages = (res.productOnHand || []).filter((s: StorageOption) => s.net_quantity > 0);

        // Keep the form selection valid after quantities changed
        const selectedID = this.borrowForm.get('storageStructureID')?.value;
        if (!this.storages.some(s => s.storageStructureID === selectedID)) {
          this.borrowForm.patchValue({
            storageStructureID: this.storages.length > 0 ? this.storages[0].storageStructureID : ''
          });
        }
        this.onStorageChange();
      },
      error: (err) => {
        console.error('Error reloading storages:', err);
      }
    });

    // Reload history
    this._picking.getAssetBorrowHistory(this.data.prdID).subscribe({
      next: (res) => {
        this.borrowHistory = res.history || [];
      },
      error: (err) => {
        console.error('Error reloading history:', err);
      }
    });
  }

  onClose(): void {
    this.dialogRef.close({ refresh: this.hasChanges });
  }

  getMaxQuantity(): number {
    const selectedStorageID = this.borrowForm.get('storageStructureID')?.value;
    if (!selectedStorageID) return 1;

    const storage = this.storages.find(s => s.storageStructureID === selectedStorageID);
    return storage ? storage.net_quantity : 1;
  }

  onStorageChange(): void {
    const maxQty = this.getMaxQuantity();
    const currentQty = this.borrowForm.get('quantity')?.value;

    if (currentQty > maxQty) {
      this.borrowForm.patchValue({ quantity: maxQty });
    }

    // Update quantity validator
    this.borrowForm.get('quantity')?.setValidators([
      Validators.required,
      Validators.min(1),
      Validators.max(maxQty)
    ]);
    this.borrowForm.get('quantity')?.updateValueAndValidity();
  }

  onAdjustmentStorageChange(): void {
    const selectedStorageID = this.adjustmentForm.get('storageStructureID')?.value;
    if (selectedStorageID) {
      const storage = this.allStorages.find(s => s.storageStructureID === selectedStorageID);
      if (storage) {
        this.adjustmentForm.patchValue({ newQuantity: storage.net_quantity || 0 });
      }
    }
  }

  onAdjust(): void {
    if (this.adjustmentForm.valid && !this.isSaving) {
      this.isSaving = true;

      const selectedStorageID = this.adjustmentForm.get('storageStructureID')?.value;
      const storage = this.allStorages.find(s => s.storageStructureID === selectedStorageID);
      const currentQty = storage?.net_quantity || 0;
      const newQty = this.adjustmentForm.get('newQuantity')?.value || 0;

      // Calculate the adjustment quantity (difference from current)
      const adjustmentQty = newQty; // The new total quantity to set

      const formData = {
        prdID: this.data.prdID,
        storageStructureID: selectedStorageID,
        quantity: adjustmentQty,
        reason: this.adjustmentForm.get('reason')?.value || null
      };

      this.subs.add(
        this._picking.assetStockAdjustment(formData).subscribe({
          next: (res) => {
            if (!res.error) {
              this.hasChanges = true;

              // Reset form
              this.adjustmentForm.reset({ newQuantity: 0 });

              // Reload data
              this.refreshData();
              this.loadAllStorages();

              // Switch to active borrows tab
              this.selectedTabIndex = 0;
            } else {
              this._toastr.error(res.message || 'Error adjusting stock');
            }
            this.isSaving = false;
          },
          error: (err) => {
            console.error('Error adjusting stock:', err);
            this._toastr.error('Error adjusting stock');
            this.isSaving = false;
          }
        })
      );
    }
  }

  getCurrentStockForSelectedStorage(): number {
    const selectedStorageID = this.adjustmentForm.get('storageStructureID')?.value;
    if (!selectedStorageID) return 0;
    const storage = this.allStorages.find(s => s.storageStructureID === selectedStorageID);
    return storage?.net_quantity || 0;
  }

  // Storage currently selected in the borrow form (falls back to the first
  // available storage, then to the storage info passed into the dialog)
  getSelectedStorage(): StorageOption | null {
    const selectedID = this.borrowForm.get('storageStructureID')?.value;
    const selected = this.storages.find(s => s.storageStructureID === selectedID);
    if (selected) {
      return selected;
    }
    if (this.storages.length > 0) {
      return this.storages[0];
    }
    if (this.data.storageInfo) {
      return {
        storageStructureID: this.data.storageInfo.storageStructureID,
        storagePath: this.data.storageInfo.storagePath,
        net_quantity: 1
      };
    }
    return null;
  }
}
